'use client';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Button } from '@quadminds/mindkit';
import useMapPermissions from '@quadminds/mindy-app/hooks/useMapPermissions';
import type { FileItem } from '@quadminds/mindy-app/types/File/File.types';
import { MoreHorizontal, Pencil, FolderInput, Download, Trash2, RefreshCw } from 'lucide-react';
import { useEffect, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

interface FileActionsProps {
    readonly file: FileItem;
    readonly onRename: (item: FileItem) => void;
    readonly onDelete: (item: FileItem) => void;
    readonly onMoveToFolder: (file: FileItem) => void;
    readonly onDownload: (file: FileItem) => void;
    readonly onReplace?: (file: FileItem) => void; // Nueva prop
    readonly iconSize?: 'sm' | 'default';
    readonly setIsViewerUser?: Dispatch<SetStateAction<boolean>>;
    readonly isMindyAdmin?: boolean;
}

export function FileActions({
    file,
    onRename,
    onDelete,
    onMoveToFolder,
    onDownload,
    onReplace,
    iconSize = 'default',
    setIsViewerUser,
    isMindyAdmin,
}: FileActionsProps) {
    const { t } = useTranslation();
    const buttonSize = iconSize === 'sm' ? 'h-7 w-7' : 'h-8 w-8';

    const { isAdmin, isEditor, isViewer } = useMapPermissions(file);

    useEffect(() => {
        if (isViewer && setIsViewerUser) {
            setIsViewerUser(true);
        }
    }, [isViewer, setIsViewerUser]);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className={buttonSize} data-testid="file-actions-button">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">{t('modals.fileOptions.actions')}</span>
                </Button>
            </DropdownMenuTrigger>
            {(isAdmin || isEditor || isMindyAdmin) && (
                <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onRename(file)} data-testid="rename-menu-item">
                        <Pencil className="h-4 w-4 mr-1" />
                        {t('modals.fileOptions.rename')}
                    </DropdownMenuItem>

                    {file.type === 'file' && (isAdmin || isEditor || isMindyAdmin) && (
                        <>
                            <DropdownMenuItem onClick={() => onMoveToFolder(file)} data-testid="move-menu-item">
                                <FolderInput className="h-4 w-4 mr-1" />
                                {t('modals.fileOptions.moveTo')}
                            </DropdownMenuItem>
                            {onReplace && (
                                <DropdownMenuItem onClick={() => onReplace(file)} data-testid="replace-menu-item">
                                    <RefreshCw className="h-4 w-4 mr-1" />
                                    {t('modals.fileOptions.replace')}
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => onDownload(file)}>
                                <Download className="h-4 w-4 mr-1" />
                                {t('modals.fileOptions.download')}
                            </DropdownMenuItem>
                        </>
                    )}

                    {(isAdmin || isMindyAdmin) && (
                        <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => onDelete(file)}
                            data-testid="delete-menu-item"
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {t('modals.fileOptions.delete')}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            )}
        </DropdownMenu>
    );
}

export default FileActions;
